/**
 * QC section (garment Increment 7, M8) — section 7 of the Style File.
 *
 * Faithful to `prototype/garment/style-file.html` #sec-qc: the summary tiles
 * (Checked · DHU · RFT · dispatch gate) over an end-line AQL record form and the
 * inspected-lot / defect log. The sampling plan (ANSI Z1.4 General Level II;
 * majors @ 2.5, minors @ 1.5) and the accept/reject verdict are computed
 * server-side from lot size + the defect lines — the form never sends a verdict.
 * Append-only: a re-inspection after rework is a new lot sharing the lot ref.
 *
 * @see prototype/garment/style-file.html #sec-qc
 * @see packages/domain/src/garment/qc
 */
import { useState } from "react";
import { ClipboardCheck, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { Button, Input, StatusPill, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { formatDate } from "../../../lib/format.js";
import type { StyleStatus } from "../lib/status.js";
import { FormSelect } from "./form-select.js";

type Severity = "critical" | "major" | "minor";
type Disposition = "rework" | "reject" | "spot" | "accept";

/** The 15-type defect preset (mirrors garment/qc DEFECT_TYPES); free-text tolerant. */
const DEFECT_TYPES = [
  "Stitch skip", "Broken stitch", "Open seam", "Puckering", "Stain / spot",
  "Shade variation", "Hole / damage", "Loose thread", "Measurement out of spec",
  "Label / care incorrect", "Button / trim defect", "Needle damage", "Pleat / fold",
  "Uneven hem", "Contamination",
];
const ZONES = ["", "Body front", "Body back", "Left sleeve", "Right sleeve", "Collar / neck", "Hem", "Shoulder", "Side seam"];

const DEFECT_OPTIONS = DEFECT_TYPES.map((t) => ({ value: t, label: t }));
const ZONE_OPTIONS = ZONES.map((z) => ({ value: z, label: z === "" ? "—" : z }));
const SEVERITY_OPTIONS = [
  { value: "critical", label: "Critical" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
];
const DISPOSITION_OPTIONS = [
  { value: "rework", label: "Rework" },
  { value: "reject", label: "Reject" },
  { value: "spot", label: "Spot-fix" },
  { value: "accept", label: "Accept" },
];

interface DefectDraft {
  defectType: string;
  zone: string;
  severity: Severity;
  qty: string;
  disposition: Disposition;
}

const emptyDefect = (): DefectDraft => ({ defectType: DEFECT_TYPES[0] ?? "Stitch skip", zone: "", severity: "major", qty: "1", disposition: "rework" });

interface QcSectionProps {
  styleId: string;
  styleStatus: StyleStatus;
}

export function QcSection({ styleId, styleStatus }: QcSectionProps) {
  const utils = trpc.useUtils();
  const query = trpc.qc.getForStyle.useQuery({ styleId });
  const [lotRef, setLotRef] = useState("");
  const [lotSize, setLotSize] = useState("");
  const [inspectedOn, setInspectedOn] = useState(() => new Date().toISOString().slice(0, 10));
  // Start with an empty defect list — the "Add defect" button inserts the
  // first row. Pre-populating one live row silently shipped a fake
  // "Stitch skip · Major · qty 1" defect on every clean-lot submission,
  // Rejecting small lots (Ac=0 codes A–D) and polluting the DB on larger
  // lots (defect row persists even when verdict = Accept).
  const [defects, setDefects] = useState<DefectDraft[]>([]);

  const record = trpc.qc.record.useMutation({
    onSuccess: async (res) => {
      setLotRef("");
      setLotSize("");
      setDefects([]);
      await Promise.all([
        utils.qc.getForStyle.invalidate({ styleId }),
        // A failed/cleared lot changes the dispatch QC-gate state → refresh Dispatch.
        utils.dispatch.getForStyle.invalidate({ styleId }),
        utils.auditLog.list.invalidate(),
      ]);
      const verdictMsg = res.verdict === "accept" ? "Lot accepted" : "Lot rejected";
      toast[res.verdict === "accept" ? "success" : "error"](`${verdictMsg} · code ${res.codeLetter} · n=${res.sampleSize}`, {
        description: `DHU ${(res.dhuTenths / 10).toFixed(1)} per 100`,
      });
    },
    onError: (e: { message: string }) => toast.error("Couldn't record inspection", { description: e.message }),
  });

  if (styleStatus !== "in_production" && styleStatus !== "shipped" && styleStatus !== "closed") {
    return (
      <div className="rounded-sm border border-dashed border-line bg-bg px-4 py-6 text-center">
        <p className="text-sm font-medium text-ink-2">QC starts once the style is in production</p>
        <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted">
          End-line AQL 2.5 inspection per lot — sample size, defect log, DHU, and accept/reject — opens when the order hits the floor.
        </p>
      </div>
    );
  }

  if (query.isLoading) return <p className="text-sm text-muted">Loading QC…</p>;
  const data = query.data;
  if (!data) return <p className="text-sm text-muted">No QC data.</p>;

  return (
    <div className="flex flex-col gap-4">
      {data.hasUnresolvedRejection ? (
        <div className="flex items-center gap-2 rounded-sm border border-bad-100 border-l-[3px] border-l-bad bg-bad-50 px-3 py-2 text-xs font-medium text-bad">
          <ShieldAlert size={14} /> A failed lot is blocking dispatch — re-inspect after rework to clear it.
        </div>
      ) : null}

      {/* Summary tiles */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Tile label="Checked" value={data.totalChecked.toLocaleString("en-IN")} sub={`of ${data.orderQty.toLocaleString("en-IN")} order`} />
        <Tile label="DHU" value={(data.dhuTenths / 10).toFixed(1)} sub="defects per 100" tone={data.dhuTenths > 80 ? "bad" : "ok"} />
        <Tile label="RFT" value={`${data.rftPercent}%`} sub="right first time" tone={data.rftPercent >= 90 ? "ok" : data.rftPercent >= 70 ? "warn" : "bad"} />
        <Tile label="FRI" value="V1.x" sub="third-party inspection" tone="muted" title="Final Random Inspection booking (SGS / Bureau Veritas) lands in V1.x" />
      </div>

      {/* Record form */}
      {data.canRecord ? (
        <div className="rounded-md border border-line bg-bg p-3">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted">Record end-line inspection</h3>
          <div className="mb-3 flex flex-wrap items-end gap-2">
            <Field label="Lot ref">
              <Input value={lotRef} onChange={(e) => { const v = e.currentTarget.value; setLotRef(v); }} placeholder="L-26-0521-04" className="h-8 w-36 text-xs" />
            </Field>
            <Field label="Lot size (pcs)">
              <Input value={lotSize} onChange={(e) => { const v = e.currentTarget.value; setLotSize(v); }} placeholder="1850" inputMode="numeric" className="h-8 w-24 text-xs" />
            </Field>
            <Field label="Inspected on">
              <input type="date" value={inspectedOn} onChange={(e) => { const v = e.currentTarget.value; setInspectedOn(v); }} className="h-8 rounded-sm border border-line bg-paper px-2 font-mono text-xs text-ink outline-none focus-visible:border-sky-600" />
            </Field>
          </div>

          {/* Defect rows */}
          <div className="flex flex-col gap-2">
            {defects.map((d, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2 rounded-sm border border-line bg-paper p-2">
                <Field label="Defect">
                  <FormSelect ariaLabel="Defect type" value={d.defectType} onValueChange={(v) => updateDefect(setDefects, i, { defectType: v })} options={DEFECT_OPTIONS} triggerClassName="h-8 w-44 px-2 text-xs" />
                </Field>
                <Field label="Zone">
                  <FormSelect ariaLabel="Zone" value={d.zone} onValueChange={(v) => updateDefect(setDefects, i, { zone: v })} options={ZONE_OPTIONS} triggerClassName="h-8 w-36 px-2 text-xs" />
                </Field>
                <Field label="Severity">
                  <FormSelect ariaLabel="Severity" value={d.severity} onValueChange={(v) => updateDefect(setDefects, i, { severity: v as Severity })} options={SEVERITY_OPTIONS} triggerClassName="h-8 w-28 px-2 text-xs" />
                </Field>
                <Field label="Qty">
                  <Input value={d.qty} onChange={(e) => { const v = e.currentTarget.value; updateDefect(setDefects, i, { qty: v }); }} inputMode="numeric" className="h-8 w-16 text-xs" />
                </Field>
                <Field label="Disposition">
                  <FormSelect ariaLabel="Disposition" value={d.disposition} onValueChange={(v) => updateDefect(setDefects, i, { disposition: v as Disposition })} options={DISPOSITION_OPTIONS} triggerClassName="h-8 w-28 px-2 text-xs" />
                </Field>
                {defects.length > 1 ? (
                  <button type="button" aria-label="Remove defect" onClick={() => setDefects((prev) => prev.filter((_, j) => j !== i))} className="mb-0.5 rounded-sm p-1.5 text-muted hover:bg-bg hover:text-bad">
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button size="sm" intent="ghost" onClick={() => setDefects((prev) => [...prev, emptyDefect()])}>
              <Plus size={14} /> Add defect
            </Button>
            <Button size="sm" disabled={record.isPending} onClick={() => submit({ styleId, lotRef, lotSize, inspectedOn, defects, record })}>
              <ClipboardCheck size={14} /> Record inspection
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted">Verdict is computed from the lot size and defect counts via ANSI Z1.4 (majors @ AQL 2.5, minors @ 1.5; any critical fails). Zero defects = a clean accept.</p>
        </div>
      ) : null}

      {/* Inspected lots */}
      {data.lots.length > 0 ? (
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted">Inspected lots</h3>
          {data.lots.map((lot) => (
            <div key={lot.id} className="rounded-md border border-line bg-paper p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm font-semibold text-ink">{lot.lotRef}</span>
                <StatusPill tone={lot.verdict === "accept" ? "ok" : "bad"} size="sm" dot>
                  {lot.verdict === "accept" ? "Accepted" : "Rejected"}
                </StatusPill>
                <span className="text-xs text-muted">
                  Code {lot.codeLetter} · n={lot.sampleSize} of {lot.lotSize.toLocaleString("en-IN")} · maj {lot.majorCount}/{lot.majorAc} · min {lot.minorCount}/{lot.minorAc} · DHU {(lot.dhuTenths / 10).toFixed(1)}
                </span>
                <span className="ml-auto text-xs text-muted">{formatDate(lot.inspectedOn)}</span>
              </div>
              {lot.defects.length > 0 ? (
                <div className="mt-2 flex flex-col gap-1">
                  {lot.defects.map((df) => (
                    <div key={df.id} className="flex items-center gap-2 rounded-sm bg-bg px-2 py-1.5 text-xs">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${df.severity === "critical" ? "bg-bad" : df.severity === "major" ? "bg-bad" : "bg-warn"}`} />
                      <span className="font-medium text-ink">{df.defectType}</span>
                      <span className="text-muted">{df.zone ?? "—"}</span>
                      <span className="ml-auto text-muted">{df.qty} pc{df.qty === 1 ? "" : "s"} · {df.disposition}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted">No defects logged — clean lot.</p>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function updateDefect(
  setDefects: React.Dispatch<React.SetStateAction<DefectDraft[]>>,
  index: number,
  patch: Partial<DefectDraft>,
) {
  setDefects((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
}

function submit({
  styleId, lotRef, lotSize, inspectedOn, defects, record,
}: {
  styleId: string;
  lotRef: string;
  lotSize: string;
  inspectedOn: string;
  defects: DefectDraft[];
  record: ReturnType<typeof trpc.qc.record.useMutation>;
}) {
  const ref = lotRef.trim();
  if (!ref) { toast.error("Enter a lot reference"); return; }
  const size = Math.round(Number(lotSize));
  if (!Number.isFinite(size) || size <= 0) { toast.error("Enter a valid lot size"); return; }

  const cleaned = defects
    .map((d) => ({
      defectType: d.defectType.trim(),
      zone: d.zone.trim() || null,
      severity: d.severity,
      qty: Math.max(0, Math.round(Number(d.qty) || 0)),
      disposition: d.disposition,
    }))
    .filter((d) => d.defectType.length > 0 && d.qty > 0);

  record.mutate({ styleId, lotRef: ref, lotSize: size, inspectedOn, defects: cleaned });
}

function Tile({ label, value, sub, tone, title }: { label: string; value: string; sub: string; tone?: "ok" | "warn" | "bad" | "muted"; title?: string }) {
  const toneCls = tone === "bad" ? "text-bad" : tone === "warn" ? "text-warn" : tone === "ok" ? "text-emerald-600" : tone === "muted" ? "text-muted" : "text-ink";
  return (
    <div className={`rounded-md border p-3 ${tone === "muted" ? "border-dashed border-line bg-bg" : "border-line bg-paper"}`} title={title}>
      <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">{label}</div>
      <div className={`mt-1 font-mono text-xl font-bold ${toneCls}`}>{value}</div>
      <div className="mt-1 text-xs text-muted">{sub}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-muted">{label}</span>
      {children}
    </label>
  );
}
